import { useEffect, useMemo, useRef, useState } from "react";

import type { ReceivedGumball, ThemeSceneProps } from "../types";

const PATH_VIEW_W = 1000;
const PATH_VIEW_H = 600;

// A wide, shallow rainbow: it overruns the viewport edges so the narrower
// gumballs stage still reads as a broad sweep beside the right sidebar.
const RAINBOW_PATH = "M -40 560 Q 500 165 1040 560";

// Six wide candy bands with generous spacing — total span ~180 viewBox units
// so the rainbow has real height and each color reads distinctly.
const RAINBOW_BANDS: ReadonlyArray<{ color: string; offset: number; width: number }> = [
  { color: "#ff6b9d", offset: -75, width: 28 },
  { color: "#ff9c4a", offset: -45, width: 28 },
  { color: "#ffd23f", offset: -15, width: 28 },
  { color: "#7bd685", offset: 15, width: 28 },
  { color: "#4ec5ff", offset: 45, width: 28 },
  { color: "#b88aff", offset: 75, width: 28 }
];

// Two white shimmer streaks per rainbow lane — each pair has different
// durations and delays so they never sync, giving the rainbow a constant
// patter of streaks coming out at semi-random intervals and speeds.
const SHIMMER_PULSES: ReadonlyArray<{
  offset: number;
  duration: number;
  delay: number;
  opacity: number;
  width: number;
}> = [
  // First streak per band
  { offset: -75, duration: 4.2, delay: 0, opacity: 0.95, width: 6 },
  { offset: -45, duration: 5.8, delay: -1.3, opacity: 0.85, width: 5 },
  { offset: -15, duration: 3.6, delay: -2.7, opacity: 0.95, width: 7 },
  { offset: 15, duration: 6.5, delay: -0.5, opacity: 0.8, width: 5 },
  { offset: 45, duration: 4.9, delay: -3.4, opacity: 0.9, width: 6 },
  { offset: 75, duration: 7.2, delay: -1.8, opacity: 0.78, width: 5 },
  // Second streak per band — different speed + offset start so the two
  // never travel together.
  { offset: -75, duration: 5.6, delay: -2.8, opacity: 0.82, width: 5 },
  { offset: -45, duration: 4.1, delay: -4.7, opacity: 0.92, width: 6 },
  { offset: -15, duration: 6.3, delay: -1.1, opacity: 0.78, width: 5 },
  { offset: 15, duration: 4.4, delay: -3.9, opacity: 0.95, width: 7 },
  { offset: 45, duration: 7.1, delay: -0.9, opacity: 0.8, width: 5 },
  { offset: 75, duration: 5.3, delay: -4.3, opacity: 0.92, width: 6 }
];

const SPARKLES = buildSparkles(90);
const SPRINKLES = buildSprinkles(34);
const SPARKLE_BURSTS = buildSparkleBursts(16);
const CLOUDS = buildClouds();
const LOLLIPOPS = buildLollipops();
const FAR_LOLLIPOPS = buildFarLollipops();
const SHOOTING_STARS = buildShootingStars();
const FLOATING_EMOJIS = buildFloatingEmojis();

const CARD_SPEED = 0.05;

export function CottonCandyScene({ received }: ThemeSceneProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardTRef = useRef<number[]>([]);
  const [pathLength, setPathLength] = useState(0);

  useEffect(() => {
    cardTRef.current = received.map((_, i) =>
      received.length === 0 ? 0 : i / received.length
    );
    cardRefs.current.length = received.length;
  }, [received.length]);

  useEffect(() => {
    const path = pathRef.current;
    if (path === null) {
      return;
    }
    const apply = () => setPathLength(path.getTotalLength());
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  useEffect(() => {
    const path = pathRef.current;
    if (path === null) {
      return;
    }
    let total = path.getTotalLength();
    let last = performance.now();
    let raf = 0;
    const onResize = () => {
      total = path.getTotalLength();
    };
    window.addEventListener("resize", onResize);
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const ts = cardTRef.current;
      for (let i = 0; i < ts.length; i++) {
        ts[i] = (ts[i]! + CARD_SPEED * dt) % 1;
        const point = path.getPointAtLength(ts[i]! * total);
        const el = cardRefs.current[i];
        if (el !== null && el !== undefined) {
          el.style.left = `${(point.x / PATH_VIEW_W) * 100}%`;
          el.style.top = `${(point.y / PATH_VIEW_H) * 100}%`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [received.length]);

  const shimmerDashArray =
    pathLength > 0 ? `${pathLength * 0.04} ${pathLength * 0.96}` : undefined;
  const shimmerDashEnd = pathLength > 0 ? -pathLength : undefined;

  return (
    <div className="ad-scene" aria-hidden="true">
      <div className="ad-scene__sky" />
      <div className="ad-scene__sun" />
      <div className="ad-scene__horizon-glow" />

      <div className="ad-scene__shooting-stars">
        {SHOOTING_STARS.map((s, i) => (
          <span
            key={i}
            className="ad-shooting-star"
            style={
              {
                top: `${s.top}%`,
                left: `${s.left}%`,
                background: `linear-gradient(90deg, transparent 0%, ${s.color}55 30%, ${s.color}cc 70%, #ffffff 100%)`,
                boxShadow: `0 0 14px ${s.color}, 0 0 4px #ffffff`,
                ["--ad-shoot-angle" as string]: `${s.angle}deg`,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.duration}s`
              } as React.CSSProperties
            }
          />
        ))}
      </div>

      <div className="ad-scene__clouds">
        {CLOUDS.map((c, i) => (
          <Cloud key={i} index={i} {...c} />
        ))}
      </div>

      <div className="ad-scene__sparkles">
        {SPARKLES.map((s, i) => (
          <span
            key={i}
            className="ad-scene__sparkle"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              backgroundColor: s.color,
              color: s.color,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`
            }}
          />
        ))}
      </div>

      <div className="ad-scene__far-props">
        {FAR_LOLLIPOPS.map((l, i) => (
          <FarLollipop key={i} {...l} />
        ))}
      </div>

      <svg
        className="ad-scene__rainbow"
        viewBox={`0 0 ${PATH_VIEW_W} ${PATH_VIEW_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <filter id="ad-rainbow-glow" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {RAINBOW_BANDS.map((band, i) => (
          <path
            key={i}
            d={RAINBOW_PATH}
            stroke={band.color}
            strokeWidth={band.width}
            fill="none"
            strokeLinecap="round"
            transform={`translate(0 ${band.offset})`}
          />
        ))}

        <path ref={pathRef} d={RAINBOW_PATH} stroke="none" fill="none" />

        {shimmerDashArray !== undefined &&
          SHIMMER_PULSES.map((pulse, i) => (
            <path
              key={i}
              d={RAINBOW_PATH}
              stroke="#ffffff"
              strokeWidth={pulse.width}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={shimmerDashArray}
              opacity={pulse.opacity}
              transform={`translate(0 ${pulse.offset})`}
              style={
                {
                  ["--ad-dash-end" as string]: `${shimmerDashEnd}`,
                  animation: `ad-shimmer ${pulse.duration}s linear infinite`,
                  animationDelay: `${pulse.delay}s`
                } as React.CSSProperties
              }
            />
          ))}
      </svg>

      <div className="ad-scene__bursts">
        {SPARKLE_BURSTS.map((b, i) => (
          <SparkleBurst key={i} {...b} />
        ))}
      </div>

      <div className="ad-scene__sprinkles">
        {SPRINKLES.map((s, i) => (
          <span
            key={i}
            className="ad-scene__sprinkle"
            style={{
              left: `${s.x}%`,
              backgroundColor: s.color,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`
            }}
          />
        ))}
      </div>

      <div className="ad-scene__props">
        {LOLLIPOPS.map((l, i) => (
          <Lollipop key={i} {...l} index={i} />
        ))}
      </div>

      <div className="ad-scene__floating-emojis">
        {FLOATING_EMOJIS.map((e, i) => (
          <span
            key={i}
            className="ad-float-emoji"
            style={{
              left: `${e.left}%`,
              fontSize: `${e.size}px`,
              animationDelay: `${e.delay}s`,
              animationDuration: `${e.duration}s`
            }}
          >
            {e.glyph}
          </span>
        ))}
      </div>

      <KawaiiUnicorn />

      <div className="ad-scene__cards">
        {received.map((entry, i) => (
          <GumballCard
            key={entry.id}
            entry={entry}
            index={i}
            elRef={(el) => {
              cardRefs.current[i] = el;
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Cloud({
  x,
  y,
  scale,
  hue,
  drift,
  index
}: {
  x: number;
  y: number;
  scale: number;
  hue: number;
  drift: number;
  index: number;
}) {
  const id = `ad-cloud-blur-${index}`;
  const c = `hsl(${hue}, 90%, 90%)`;
  const c2 = `hsl(${hue}, 80%, 80%)`;
  return (
    <svg
      className="ad-cloud"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${220 * scale}px`,
        animationDuration: `${drift}s`,
        animationDelay: `${-drift * (index / 5)}s`
      }}
      viewBox="0 0 240 110"
      aria-hidden="true"
    >
      <defs>
        <filter id={id} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      <g filter={`url(#${id})`}>
        <ellipse cx="60" cy="70" rx="44" ry="26" fill={c2} opacity="0.9" />
        <ellipse cx="105" cy="55" rx="50" ry="34" fill={c} />
        <ellipse cx="155" cy="65" rx="46" ry="28" fill={c2} opacity="0.9" />
        <ellipse cx="195" cy="72" rx="36" ry="22" fill={c2} opacity="0.85" />
        <ellipse cx="90" cy="48" rx="22" ry="14" fill="white" opacity="0.65" />
        <ellipse cx="135" cy="42" rx="20" ry="13" fill="white" opacity="0.6" />
      </g>
    </svg>
  );
}

function SparkleBurst({
  x,
  y,
  size,
  color,
  delay,
  duration
}: {
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
}) {
  return (
    <svg
      className="ad-burst"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        filter: `drop-shadow(0 0 ${size * 0.4}px ${color})`
      }}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M 12 0 L 13.5 10.5 L 24 12 L 13.5 13.5 L 12 24 L 10.5 13.5 L 0 12 L 10.5 10.5 Z"
        fill={color}
      />
      <circle cx="12" cy="12" r="1.6" fill="#ffffff" />
    </svg>
  );
}

function Lollipop({
  x,
  y,
  hue,
  scale,
  index
}: {
  x: number;
  y: number;
  hue: number;
  scale: number;
  index: number;
}) {
  const headSize = 64;
  const c1 = `hsl(${hue}, 90%, 65%)`;
  const c2 = `hsl(${(hue + 60) % 360}, 90%, 78%)`;
  return (
    <div
      className="ad-lolli"
      style={{
        left: `${x}%`,
        bottom: `${y}%`,
        transform: `scale(${scale})`
      }}
    >
      <div
        className="ad-lolli__bob"
        style={{ animationDelay: `${index * -0.6}s` }}
      >
        <div
          className="ad-lolli__head"
          style={{
            width: `${headSize}px`,
            height: `${headSize}px`,
            background: `conic-gradient(from 0deg, ${c1} 0deg, ${c2} 45deg, ${c1} 90deg, ${c2} 135deg, ${c1} 180deg, ${c2} 225deg, ${c1} 270deg, ${c2} 315deg, ${c1} 360deg)`,
            boxShadow: `0 0 28px ${c1}cc, inset -5px -5px 12px rgba(0,0,0,0.18), inset 4px 4px 8px rgba(255,255,255,0.65)`
          }}
        />
        <div
          className="ad-lolli__stick"
          style={{
            width: `${headSize * 0.1}px`,
            height: `${headSize * 1.7}px`
          }}
        />
      </div>
    </div>
  );
}

function FarLollipop({ x, y, hue, scale }: { x: number; y: number; hue: number; scale: number }) {
  const headSize = 28;
  const c = `hsl(${hue}, 80%, 72%)`;
  return (
    <div
      className="ad-far-lolli"
      style={{
        left: `${x}%`,
        bottom: `${y}%`,
        transform: `scale(${scale})`,
        opacity: 0.55
      }}
    >
      <div
        className="ad-far-lolli__head"
        style={{
          width: `${headSize}px`,
          height: `${headSize}px`,
          background: c,
          boxShadow: `0 0 12px ${c}80`
        }}
      />
      <div
        className="ad-far-lolli__stick"
        style={{
          width: `${headSize * 0.12}px`,
          height: `${headSize * 1.8}px`
        }}
      />
    </div>
  );
}

function KawaiiUnicorn() {
  // Use the unicorn emoji — every platform ships a polished, expressive
  // unicorn glyph that reads instantly. CSS handles the bobbing and the
  // glow halo behind it.
  return (
    <div className="ad-mascot" aria-hidden="true">
      <div className="ad-mascot__halo" />
      <div className="ad-mascot__bob">
        <span className="ad-mascot__emoji" role="img" aria-label="unicorn">
          🦄
        </span>
        <span className="ad-mascot__sparkle ad-mascot__sparkle--1" aria-hidden="true">
          ✨
        </span>
        <span className="ad-mascot__sparkle ad-mascot__sparkle--2" aria-hidden="true">
          ✨
        </span>
        <span className="ad-mascot__sparkle ad-mascot__sparkle--3" aria-hidden="true">
          ⭐
        </span>
      </div>
    </div>
  );
}

function GumballCard({
  entry,
  index,
  elRef
}: {
  entry: ReceivedGumball;
  index: number;
  elRef: (el: HTMLDivElement | null) => void;
}) {
  const count = Math.max(1, Math.min(entry.amount, 8));
  const columns = Math.min(count, 4);
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);
  return (
    <div ref={elRef} className="ad-card">
      <div
        className="ad-card__bob"
        style={{ animationDelay: `${(index % 5) * -0.7}s` }}
      >
        <div
          className="ad-card__cluster"
          style={{ gridTemplateColumns: `repeat(${columns}, 18px)` }}
        >
          {pieces.map((i) => (
            <Gumball key={i} variant={(index + i) % 6} index={index * 7 + i} />
          ))}
        </div>
        <div className="ad-card__tooltip" role="tooltip">
          <span className="ad-card__from">{entry.from}</span>
          <span className="ad-card__amount">{entry.amount}</span>
          {entry.reason !== null && entry.reason.trim() !== "" && (
            <div className="ad-card__note">{entry.reason}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Gumball({ variant, index }: { variant: number; index: number }) {
  const palettes: ReadonlyArray<readonly [string, string]> = [
    ["#ff6b9d", "#ff9c4a"],
    ["#ff9c4a", "#ffd23f"],
    ["#ffd23f", "#7bd685"],
    ["#7bd685", "#4ec5ff"],
    ["#4ec5ff", "#b88aff"],
    ["#b88aff", "#ff6b9d"]
  ];
  const [c1, c2] = palettes[variant % palettes.length]!;
  const id = `ad-gb-${index}`;
  return (
    <svg
      className="ad-card__piece"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={id} cx="32%" cy="28%" r="80%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="32%" stopColor={c1} />
          <stop offset="100%" stopColor={c2} />
        </radialGradient>
      </defs>
      <circle cx="10" cy="10" r="9" fill={`url(#${id})`} />
      <ellipse cx="6.5" cy="6" rx="3" ry="2.4" fill="rgba(255,255,255,0.75)" />
      <circle cx="14" cy="14.5" r="1" fill="rgba(255,255,255,0.6)" />
    </svg>
  );
}

// ---------- deterministic seeded layouts ----------------------------------

function buildSparkles(count: number) {
  const out: Array<{
    x: number;
    y: number;
    size: number;
    delay: number;
    duration: number;
    color: string;
  }> = [];
  const COLORS = ["#ffffff", "#fff5d4", "#ffe6f2", "#dde6ff", "#ffeed4", "#ffd0e8"];
  let s = 0xa11ce;
  for (let i = 0; i < count; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const x = (s % 1000) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const y = (s % 700) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const size = 1.5 + (s % 35) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const delay = -((s % 60) / 10);
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const duration = 2.4 + (s % 28) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const color = COLORS[s % COLORS.length]!;
    out.push({ x, y, size, delay, duration, color });
  }
  return out;
}

function buildSprinkles(count: number) {
  const out: Array<{
    x: number;
    delay: number;
    duration: number;
    color: string;
  }> = [];
  const COLORS = ["#ff6b9d", "#ff9c4a", "#ffd23f", "#7bd685", "#4ec5ff", "#b88aff", "#ffffff"];
  let s = 0xfeed;
  for (let i = 0; i < count; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const x = (s % 1000) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const duration = 5 + (s % 60) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const delay = -((s % 100) / 10);
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const color = COLORS[s % COLORS.length]!;
    out.push({ x, delay, duration, color });
  }
  return out;
}

function buildSparkleBursts(count: number) {
  const out: Array<{
    x: number;
    y: number;
    color: string;
    size: number;
    delay: number;
    duration: number;
  }> = [];
  const COLORS = ["#ffffff", "#fff5d4", "#ffe6f2"];
  let s = 0xbabe;
  for (let i = 0; i < count; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const x = 8 + (s % 850) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const y = 14 + (s % 500) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const size = 18 + (s % 18);
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const delay = -((s % 40) / 10);
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const duration = 2.2 + (s % 28) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const color = COLORS[s % COLORS.length]!;
    out.push({ x, y, color, size, delay, duration });
  }
  return out;
}

function buildClouds() {
  return [
    { x: -10, y: 6, scale: 1.3, hue: 320, drift: 110 },
    { x: 25, y: 14, scale: 1.0, hue: 280, drift: 85 },
    { x: 55, y: 4, scale: 1.5, hue: 30, drift: 130 },
    { x: 80, y: 18, scale: 1.1, hue: 200, drift: 95 },
    { x: 5, y: 22, scale: 0.9, hue: 50, drift: 75 }
  ];
}

function buildLollipops() {
  return [
    { x: 6, y: 5, hue: 320, scale: 1.0 },
    { x: 22, y: 3, hue: 30, scale: 1.1 },
    { x: 76, y: 4, hue: 160, scale: 1.05 },
    { x: 92, y: 6, hue: 270, scale: 0.95 }
  ];
}

function buildFarLollipops() {
  return [
    { x: 12, y: 26, hue: 320, scale: 0.9 },
    { x: 30, y: 28, hue: 50, scale: 0.85 },
    { x: 48, y: 30, hue: 200, scale: 0.95 },
    { x: 64, y: 27, hue: 280, scale: 0.9 },
    { x: 82, y: 29, hue: 160, scale: 0.85 }
  ];
}

function buildShootingStars() {
  // Streaks fall from upper-left toward lower-right. Positive rotation
  // points the streak's bright head along the motion vector so the fading
  // trail correctly drags behind.
  return [
    { top: 4, left: -10, angle: 24, color: "#ff6b9d", duration: 3.5, delay: 0 },
    { top: 12, left: -8, angle: 28, color: "#4ec5ff", duration: 5.2, delay: -2.4 },
    { top: 0, left: -12, angle: 22, color: "#ffd23f", duration: 4.1, delay: -1.1 },
    { top: 18, left: -10, angle: 32, color: "#b88aff", duration: 6.0, delay: -3.7 },
    { top: 8, left: -10, angle: 26, color: "#7bd685", duration: 4.8, delay: -5.3 }
  ];
}

function buildFloatingEmojis() {
  // Mix of sparkle-like emoji rising slowly from below, drifting up like
  // magic embers. Cadences and sizes vary so the layer never feels grid-like.
  const glyphs = ["✨", "🌈", "⭐", "💫", "✨", "💖", "🌟", "✨"];
  const out: Array<{
    glyph: string;
    left: number;
    size: number;
    delay: number;
    duration: number;
  }> = [];
  let s = 0xc0de;
  for (let i = 0; i < glyphs.length; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const left = (s % 1000) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const size = 18 + (s % 14);
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const duration = 8 + (s % 60) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const delay = -((s % 100) / 10);
    out.push({ glyph: glyphs[i]!, left, size, delay, duration });
  }
  return out;
}
